import React, {useEffect, useMemo, useRef, useState} from "react";
import {useNavigate} from "react-router-dom";
import {useSelector} from "react-redux";
import {Button} from "primereact/button";
import {Card} from "primereact/card";
import {Message} from "primereact/message";
import {Skeleton} from "primereact/skeleton";
import {useTranslate} from "../../system/useTranslate";
import {useReportDashboard} from "../../models/report-dashboard/use-report-dashboard";
import ReportDashboardDialog from "./report-dashboard-dialog";
import {relationId, toArray, valueId} from "./report-utils";
import {richTextToPlain} from "../../system/components/rich-text";

function dashboardDate(value, fallback) {
    return value ? new Date(value).toLocaleDateString("fa-IR") : fallback;
}

function DashboardCard({dashboard, translate, onOpen, onEdit}) {
    const reportsCount = toArray(dashboard?.reports).length;
    const updatedAt = dashboardDate(dashboard?.updatedAt || dashboard?.createdAt, translate("reports.dashboard.createdAt"));

    const header = (
        <div className="flex align-items-center justify-content-between gap-2 p-3 pb-0">
            <Button
                icon="pi pi-pencil"
                text
                rounded
                size="small"
                severity="secondary"
                aria-label={translate("reports.dashboard.edit")}
                onClick={(event) => {
                    event.stopPropagation();
                    onEdit(dashboard);
                }}
            />
            <span className="inline-flex align-items-center justify-content-center w-3rem h-3rem border-round surface-100 text-primary border-1 border-primary">
                <i className="pi pi-chart-bar text-xl"/>
            </span>
        </div>
    );

    const footer = (
        <div className="flex align-items-center justify-content-between gap-2 pt-2 border-top-1 border-200 text-xs text-500">
            <span>{updatedAt}</span>
            <span>{reportsCount} {translate("reports.dashboard.reportCount")}</span>
            <Button
                icon="pi pi-angle-left"
                text
                rounded
                size="small"
                severity="secondary"
                aria-label={translate("reports.dashboard.open")}
                onClick={(event) => {
                    event.stopPropagation();
                    onOpen(dashboard);
                }}
            />
        </div>
    );

    return (
        <Card
            className="h-full border-1 border-200 shadow-none cursor-pointer"
            header={header}
            footer={footer}
            onClick={() => onOpen(dashboard)}
        >
            <div className="flex flex-column gap-2 text-right">
                <h3 className="m-0 text-base font-medium text-900">{dashboard?.title}</h3>
                <p className="m-0 text-sm line-height-3 text-700 overflow-hidden" style={{display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical"}}>{richTextToPlain(dashboard?.description)}</p>
            </div>
        </Card>
    );
}

function Index() {
    const translate = useTranslate();
    const navigate = useNavigate();
    const {workspaceMembershipData} = useSelector((state) => state.app);
    const dashboardService = useReportDashboard();
    const dashboardServiceRef = useRef(dashboardService);

    const workspaceId = workspaceMembershipData?.workspace?.id;
    const ownerId = workspaceMembershipData?.id;

    const [viewMode, setViewMode] = useState("grid");
    const [dashboardDialogVisible, setDashboardDialogVisible] = useState(false);
    const [selectedDashboard, setSelectedDashboard] = useState(null);
    const [reportDashboards, setReportDashboards] = useState([]);
    const [dashboardsState, setDashboardsState] = useState({
        loading: false,
        error: false,
        saving: false,
        saveError: false,
    });

    useEffect(() => {
        dashboardServiceRef.current = dashboardService;
    }, [dashboardService]);

    useEffect(() => {
        let mounted = true;
        if (!workspaceId) {
            setReportDashboards([]);
            setDashboardsState((current) => ({...current, loading: false, error: false}));
            return () => {
                mounted = false;
            };
        }

        setDashboardsState((current) => ({...current, loading: true, error: false}));
        dashboardServiceRef.current.getReportDashboards(workspaceId).then((dashboards) => {
            if (!mounted) return;
            setReportDashboards(toArray(dashboards));
            setDashboardsState((current) => ({...current, loading: false, error: false}));
        }).catch((error) => {
            if (!mounted) return;
            setReportDashboards([]);
            setDashboardsState((current) => ({
                ...current,
                loading: false,
                error: error?.message || true,
            }));
        });

        return () => {
            mounted = false;
        };
    }, [workspaceId]);

    const sortedDashboards = useMemo(() => toArray(reportDashboards), [reportDashboards]);

    const openDashboardDialog = (dashboard = null) => {
        setSelectedDashboard(dashboard);
        setDashboardsState((current) => ({...current, saveError: false}));
        setDashboardDialogVisible(true);
    };

    const openDashboard = (dashboard) => {
        const dashboardDocumentId = valueId(dashboard);
        if (dashboardDocumentId) navigate(`/reports/${dashboardDocumentId}`);
    };

    const handleSaveDashboard = async ({value}) => {
        if (!workspaceId) {
            setDashboardsState((current) => ({...current, saveError: translate("reports.dashboard.noWorkspace")}));
            return;
        }

        setDashboardsState((current) => ({...current, saving: true, saveError: false}));
        try {
            const title = value.title?.trim();
            const payload = {
                title,
                description: value.description?.trim() || "",
            };
            const savedDashboard = selectedDashboard
                ? await dashboardServiceRef.current.updateReportDashboard(valueId(selectedDashboard), payload)
                : await dashboardServiceRef.current.createReportDashboard({
                    ...payload,
                    workspace: workspaceId,
                    owner: ownerId,
                });

            setReportDashboards((current) => {
                if (!selectedDashboard) return [savedDashboard, ...current].filter(Boolean);
                const selectedId = valueId(selectedDashboard);
                return current.map((dashboard) => valueId(dashboard) === selectedId ? (savedDashboard || {...dashboard, ...payload}) : dashboard);
            });
            setDashboardsState((current) => ({...current, saving: false, saveError: false}));
            setDashboardDialogVisible(false);
            setSelectedDashboard(null);
        } catch (error) {
            setDashboardsState((current) => ({
                ...current,
                saving: false,
                saveError: error?.message || translate("reports.dashboard.createError"),
            }));
        }
    };

    return (
        <div className="p-3 md:p-4 surface-ground min-h-screen" dir="rtl">
            <div className="flex flex-column md:flex-row align-items-start md:align-items-center justify-content-between gap-3 mb-4">
                <div className="flex flex-column gap-2 text-right">
                    <span className="text-xs text-primary font-bold">{translate("reports.page.eyebrow")}</span>
                    <h1 className="m-0 text-3xl font-bold text-900">{translate("reports.page.title")}</h1>
                    <p className="m-0 text-base line-height-3 text-700">{translate("reports.page.description")}</p>
                </div>
                <Button
                    label={translate("reports.page.addDashboard")}
                    icon="pi pi-plus"
                    onClick={() => openDashboardDialog()}
                    size="small"
                    className="text-xs"
                    loading={dashboardsState.saving}
                />
            </div>

            <div className="flex align-items-center justify-content-between gap-2 mb-3 pb-3 border-bottom-1 border-200">
                <div className="flex align-items-center gap-2 text-900">
                    <i className="pi pi-chart-bar text-primary"/>
                    <h2 className="m-0 text-xl font-bold">{translate("reports.page.recent")}</h2>
                </div>
                <div className="flex align-items-center gap-1">
                    <Button
                        icon="pi pi-list"
                        text
                        rounded
                        size="small"
                        severity={viewMode === "list" ? undefined : "secondary"}
                        aria-label={translate("reports.page.listView")}
                        onClick={() => setViewMode("list")}
                    />
                    <Button
                        icon="pi pi-th-large"
                        text
                        rounded
                        size="small"
                        severity={viewMode === "grid" ? undefined : "secondary"}
                        aria-label={translate("reports.page.gridView")}
                        onClick={() => setViewMode("grid")}
                    />
                </div>
            </div>

            {dashboardsState.error && (
                <Message severity="error" text={translate("reports.dashboard.loadError")} className="mb-3 text-xs"/>
            )}

            {dashboardsState.saveError && (
                <Message
                    severity="error"
                    text={dashboardsState.saveError === true ? translate("reports.dashboard.createError") : dashboardsState.saveError}
                    className="mb-3 text-xs"
                />
            )}

            {dashboardsState.loading && (
                <div className="grid">
                    {[1, 2, 3].map((item) => (
                        <div className={viewMode === "list" ? "col-12" : "col-12 md:col-6 lg:col-4"} key={item}>
                            <div className="surface-card border-1 border-200 border-round p-3 h-full flex flex-column gap-3">
                                <Skeleton width="3rem" height="3rem"/>
                                <Skeleton width="70%" height="1.25rem"/>
                                <Skeleton width="90%" height="1rem"/>
                                <Skeleton width="100%" height="2.5rem"/>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {!dashboardsState.loading && (
                <div className="grid">
                    <div className={viewMode === "list" ? "col-12" : "col-12 md:col-6 lg:col-4"}>
                        <Card className="h-full border-1 border-dashed border-300 shadow-none">
                            <div className="flex flex-column align-items-center justify-content-center gap-2 text-center p-3">
                                <Button
                                    icon="pi pi-plus"
                                    rounded
                                    outlined
                                    size="small"
                                    aria-label={translate("reports.dashboard.empty.title")}
                                    onClick={() => openDashboardDialog()}
                                    loading={dashboardsState.saving}
                                />
                                <strong className="text-sm font-medium text-900">{translate("reports.dashboard.empty.title")}</strong>
                                <small className="text-xs text-600">{translate("reports.dashboard.empty.description")}</small>
                            </div>
                        </Card>
                    </div>
                    {sortedDashboards.map((dashboard) => (
                        <div className={viewMode === "list" ? "col-12" : "col-12 md:col-6 lg:col-4"} key={valueId(dashboard) || relationId(dashboard)}>
                            <DashboardCard
                                dashboard={dashboard}
                                translate={translate}
                                onOpen={openDashboard}
                                onEdit={openDashboardDialog}
                            />
                        </div>
                    ))}
                </div>
            )}

            <ReportDashboardDialog
                visible={dashboardDialogVisible}
                setVisible={(visible) => {
                    setDashboardDialogVisible(visible);
                    if (!visible) setSelectedDashboard(null);
                }}
                dashboardData={selectedDashboard}
                onSubmit={handleSaveDashboard}
                loading={dashboardsState.saving}
            />
        </div>
    );
}

export default Index;
